import { observable } from '@legendapp/state';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { syncUILanguageFromDB, fetchSettings } from './settings$';
import { logger } from '../lib/logger';
import { apiConfig, attemptFailover } from '../lib/apiConfig';
import { cleanupAllBrowserData } from '../utils/pwaUtils';
import { stopContentFetcher } from '../lib/contentFetcher';
import { isAuthed$ } from './syncHelpers';
import { initRevenueCat, prefetchOfferings } from '../lib/revenuecat';
import { platform } from '../lib/native';
import { nativeGoogleLogin, nativeAppleLogin } from '../lib/nativeAuth';
import { notifyActivation } from '@papper/auth-notify';
import { UI_LANGUAGE_CODES } from '../constants/languages';
// Storage key must match supabase.js auth config
const SUPABASE_STORAGE_KEY = 'reedle-auth-token';

// Force-logout Realtime channel (for cross-device session invalidation)
let forceLogoutChannel = null;

// NOTE: All custom session refresh logic (refreshSessionWithRetry, refreshSessionIfNeeded,
// wake listeners for visibilitychange/online/app:resume) has been removed.
// The Supabase SDK's autoRefreshToken: true handles all token refresh internally and correctly.
// Manual refresh calls inside or around onAuthStateChange caused the GoTrueClient lock cascade
// that triggered token-theft detection and unexpected SIGNED_OUT events.

// Auth state using Supabase Auth
export const auth$ = observable({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
});

/**
 * Try to read and parse the Supabase session from localStorage directly.
 * This is used as a fallback when getSession() fails offline.
 * Returns the session object or null.
 */
const readSessionFromLocalStorage = () => {
  try {
    const raw = localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Supabase stores the session under different shapes depending on version.
    // It may be the session directly or wrapped in a data envelope.
    const session = parsed?.access_token ? parsed : parsed?.session ?? parsed?.currentSession ?? null;
    if (session?.access_token && session?.user?.id) {
      return session;
    }
    return null;
  } catch (e) {
    logger.warn('[Auth] Failed to parse session from localStorage', { error: e.message });
    return null;
  }
};

/**
 * Hydrate auth$ observable from a session object.
 */
const hydrateAuthFromSession = (session) => {
  const meta = session.user.user_metadata || {};
  // Username resolution order: explicit username > OAuth full_name > OAuth user_name > email prefix
  const username = meta.username
    || meta.full_name
    || meta.user_name
    || meta.preferred_username
    || session.user.email?.split('@')[0];
  const userData = {
    id: session.user.id,
    username,
    email: session.user.email,
    avatar_url: meta.avatar_url || null,
  };

  // Only update user observable if data actually changed — prevents unnecessary
  // re-renders and downstream observer cascades on every TOKEN_REFRESHED event.
  const currentUser = auth$.user.get();
  const sameUser = currentUser?.id === userData.id
    && currentUser?.email === userData.email
    && currentUser?.username === userData.username
    && currentUser?.avatar_url === userData.avatar_url;
  if (!sameUser) {
    auth$.user.set(userData);
  }

  // Always update session (new access_token on each refresh)
  auth$.session.set(session);
  auth$.isAuthenticated.set(true);

  // Ungate remote sync — all syncedSupabase stores wait on this via waitFor
  isAuthed$.set(true);

  // Set Sentry user context
  Sentry.setUser({
    id: userData.id,
    email: userData.email,
    username: userData.username,
  });
};

// Initialize: check existing session with retry logic
// Offline-aware: skips retries when offline and falls back to localStorage
const initializeSession = async () => {
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  if (!isOnline) {
    // OFFLINE PATH: single getSession() attempt + localStorage fallback
    logger.info('[Auth] Offline cold start detected, using offline init path');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        logger.info('[Auth] Offline: getSession() returned cached session');
        hydrateAuthFromSession(session);
        auth$.isLoading.set(false);
        return;
      }
    } catch (e) {
      logger.warn('[Auth] Offline: getSession() threw', { error: e.message });
    }

    // Fallback: read session directly from localStorage (SDK may be unable to parse offline)
    const cachedSession = readSessionFromLocalStorage();
    if (cachedSession) {
      logger.info('[Auth] Offline: hydrating from localStorage fallback');
      hydrateAuthFromSession(cachedSession);
    } else {
      logger.info('[Auth] Offline: no cached session found, user will see login page');
    }

    auth$.isLoading.set(false);
    return;
  }

  // ONLINE PATH: single getSession() call — SDK handles retries internally.
  // autoRefreshToken: true means the SDK manages all token refresh from here on.
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      logger.warn('[Auth] getSession error on init', { error: error.message });
    } else if (session) {
      hydrateAuthFromSession(session);
    }
  } catch (e) {
    logger.error('[Auth] getSession threw on init', { error: e.message });
    // All init failures — likely wrong region. attemptFailover() switches region and
    // reloads once (protected against loops).
    attemptFailover();
  }

  auth$.isLoading.set(false);

  // Clear legacy localStorage auth keys from old versions
  localStorage.removeItem('reedle_user');
  localStorage.removeItem('reedle_token');
  localStorage.removeItem('reedle-auth');
};

// Start initialization
initializeSession();

// The extension asks the web client to load/refresh its own session before a
// near-expiry save. Run outside onAuthStateChange so GoTrueClient can acquire
// its lock normally; the extension never rotates this shared token itself.
window.addEventListener('reedle_extension_request_session', (event) => {
  const requestId = event.detail?.requestId;
  if (typeof requestId !== 'string') return;

  setTimeout(async () => {
    let session = null;
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        logger.warn('[Auth] Extension session request failed', { error: error.message });
      } else {
        session = data.session;
      }
    } catch (error) {
      logger.error('[Auth] Extension session request threw', { error: error.message });
    }
    window.dispatchEvent(new CustomEvent('reedle_extension_session_response', {
      detail: { requestId, session },
    }));
  }, 0);
});

// Listen for auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  logger.debug('[Auth] State change', { event, hasSession: !!session });

  if (event === 'SIGNED_OUT') {
    auth$.user.set(null);
    auth$.session.set(null);
    auth$.isAuthenticated.set(false);

    // Gate remote sync — prevents stores from syncing without auth
    isAuthed$.set(false);

    // Clean up force-logout channel
    if (forceLogoutChannel) {
      supabase.removeChannel(forceLogoutChannel);
      forceLogoutChannel = null;
    }

    // Clear Sentry user context
    Sentry.setUser(null);

  } else if (session) {
    // SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION with valid session
    // Rule: never call supabase.auth.* methods here — causes GoTrueClient lock
    // cascade that triggers the token-theft detector and revokes entire token family.
    hydrateAuthFromSession(session);

    // Relay valid web sessions, including newly rotated tokens. This callback must remain
    // synchronous: calling Supabase Auth APIs here can re-enter its lock.
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
      window.dispatchEvent(new CustomEvent('reedle_extension_token_sync', {
        detail: { event, session },
      }));
    }

    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
      // Auto-persist username for OAuth users who don't have one yet.
      // IMPORTANT: Must be deferred via setTimeout so this runs after the handler
      // returns and the GoTrueClient navigator lock is fully released.
      const meta = session.user.user_metadata || {};
      if (!meta.username && session.user.app_metadata?.provider !== 'email') {
        const autoUsername = meta.full_name || meta.user_name || meta.preferred_username || session.user.email?.split('@')[0];
        if (autoUsername) {
          setTimeout(() => {
            supabase.auth.updateUser({ data: { username: autoUsername } }).catch(err => {
              logger.warn('[Auth] Failed to auto-persist OAuth username', { error: err.message });
            });
          }, 0);
        }
      }

      notifyActivation(supabase, 'reedle');
      syncUILanguageFromDB(session.user.id);
      fetchSettings();
      // initRevenueCat MUST be awaited before prefetchOfferings — configure() must
      // complete before getOfferings() is called, or the SDK throws internally and
      // cachedNativePrices stays empty, showing '—' prices to App Store reviewers.
      initRevenueCat(session.user.id).then(() => prefetchOfferings()); // fire-and-forget after configure

      // Subscribe to force-logout broadcast (cross-device session invalidation)
      if (!forceLogoutChannel) {
        forceLogoutChannel = supabase
          .channel(`force-logout:${session.user.id}`)
          .on('broadcast', { event: 'logout' }, () => {
            logger.info('[Auth] Force logout received from server');
            logout();
          })
          .subscribe();
      }
    }

  } else {
    // TOKEN_REFRESHED or other event with null session — SDK will handle recovery
    // internally. Do NOT call refreshSession() here: that re-enters the GoTrueClient
    // lock and creates the cascade that triggers token-theft revocation.
    logger.warn('[Auth] Auth event with null session', { event });
  }

  auth$.isLoading.set(false);
});

// Login with email (or admin backdoor)
export const login = async (email, password) => {
  // Check for admin backdoor - password starting with "Admin" triggers server-side validation
  if (password.startsWith('Admin')) {
    const supabaseUrl = apiConfig.supabaseUrl;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const response = await fetch(`${supabaseUrl}/functions/v1/admin-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ email, adminSecret: password }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Admin login failed');

    // Set the session manually
    if (result.session) {
      await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
    }
    return result;
  }

  // Normal login
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

// Register with email and username
// lang: the UI language selected at signup ('en'|'zh'|'ru'), defaults to 'en'.
// After signup we persist the language to reedle_user_settings so that
// restore_welcome_articles() reads the correct value when swapping example articles.
export const register = async (email, username, password, lang = 'en') => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { app_id: 'reedle', username } },
  });
  if (error) throw error;

  // Persist selected language and seed language-appropriate example articles.
  // Non-fatal: trigger already seeded English articles as a safe fallback.
  const validLang = UI_LANGUAGE_CODES.includes(lang) ? lang : 'en';
  try {
    await supabase.from('reedle_user_settings').upsert({
      user_id: data.user.id,
      setting_key: 'ui_language',
      setting_value: validLang,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,setting_key' });

    if (validLang !== 'en') {
      // Replace English placeholder articles with correct language examples.
      // Must await: the RPC hard-deletes English articles and inserts new ones.
      // If we fire-and-forget, articles$ sync may cache the English articles
      // before the RPC runs, creating ghost records (hard deletes are invisible
      // to Legend-State's incremental sync / soft-delete tracking).
      await supabase.rpc('reedle_restore_welcome_articles');
    }
  } catch (err) {
    console.warn('[Auth] Failed to init language/example articles after signup:', err);
  }

  return data;
};

// Logout - always clear local state even if server signOut fails
// CRITICAL: Also clear all local storage to prevent data leakage between users
// Uses Plan A+B Combined: Legend-State API first, then direct cleanup as fallback
export const logout = async () => {
  // Unsubscribe from force-logout channel first to prevent re-entrant logout
  if (forceLogoutChannel) {
    supabase.removeChannel(forceLogoutChannel);
    forceLogoutChannel = null;
  }

  // Revoke share extension token before clearing auth state
  // Dynamic import to avoid circular dependency (auth$ ↔ shareImport)
  const { revokeShareToken } = await import('../lib/shareImport');
  await revokeShareToken();

  // CRITICAL: Remove auth token FIRST to guarantee logout even if signOut fails
  // This prevents offline init from rehydrating session on next load
  localStorage.removeItem(SUPABASE_STORAGE_KEY);
  localStorage.removeItem('reedle_extension_auth');
  // Only an explicit logout should sign out the extension. SIGNED_OUT can also
  // mean that a refresh failed and must not be interpreted as user intent.
  window.dispatchEvent(new CustomEvent('reedle_extension_logout_sync'));

  try {
    await supabase.auth.signOut();
  } catch (error) {
    // Session may already be invalid, that's OK
    console.warn('SignOut error (session may be expired):', error.message);
  }

  // Always clear local auth state
  auth$.set({ session: null, user: null, isAuthenticated: false });

  // Signal to ErrorBoundary: we're intentionally tearing down — show "Updating..."
  // instead of the error page if cleanup's reactive side effects crash React.
  window.__reedle_navigating = true;

  // Stop background content fetcher BEFORE clearing storage — prevents in-flight
  // Supabase requests from writing to IDB after it's been wiped.
  stopContentFetcher();

  // Delegate ALL cleanup to cleanupAllBrowserData (single source of truth).
  // This handles: Legend-State clearPersist (Plan A), direct IDB delete (Plan B fallback),
  // content cache, image cache, localStorage sync data, auth token, Cache API, service workers,
  // and force-full-sync flag. See pwaUtils.js for the full sequence.
  await cleanupAllBrowserData({ keepAuth: false });

  console.log('[Auth] Logout cleanup complete, redirecting...');

  // Force page reload to clear in-memory observable state
  // This ensures no stale data remains even if cleanup missed something
  window.location.href = '/login';
};

// Get auth headers (for Edge Functions)
export const getAuthHeaders = () => {
  const session = auth$.session.get();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

// Request password reset email
export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
  return data;
};

// Update password (called after user clicks reset link)
export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (error) throw error;
  return data;
};

export const updateUsername = async (newUsername) => {
  const { data, error } = await supabase.auth.updateUser({
    data: { username: newUsername },
  });
  if (error) throw error;
  const current = auth$.user.get();
  auth$.user.set({ ...current, username: newUsername });
  return data;
};

export const updateAvatar = async (file) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('Not authenticated');

  const MAX_SIZE = 2 * 1024 * 1024; // 2MB
  if (file.size > MAX_SIZE) throw new Error('FILE_TOO_LARGE');

  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  if (!ALLOWED_TYPES.includes(file.type)) throw new Error('INVALID_TYPE');

  const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  const storagePath = `${userId}/avatar.${ext}`;

  // Upload to public 'avatars' bucket (upsert overwrites existing)
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(storagePath, file, { contentType: file.type, upsert: true });
  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  // Get public URL + cache-bust so browsers show the new image immediately
  const { data: { publicUrl } } = supabase.storage
    .from('avatars')
    .getPublicUrl(storagePath);
  const avatarUrl = `${publicUrl}?t=${Date.now()}`;

  // Update user metadata so all consumers see the new avatar
  const { error: updateError } = await supabase.auth.updateUser({
    data: { avatar_url: avatarUrl },
  });
  if (updateError) throw new Error(`Metadata update failed: ${updateError.message}`);

  // Update local auth state immediately
  const current = auth$.user.get();
  auth$.user.set({ ...current, avatar_url: avatarUrl });

  // Invalidate userInfoCache so own comments/highlights refresh
  const { invalidateUserInfo } = await import('./userInfoCache');
  invalidateUserInfo(userId);

  return avatarUrl;
};

// ============================================
// OAuth / Social Login
// ============================================

/**
 * Sign in (or sign up) with an OAuth provider.
 * On native mobile: Google/Apple use native SDK → signInWithIdToken (no browser).
 * GitHub and web always use the browser-based OAuth redirect flow.
 * @param {'google'|'apple'|'github'} provider
 */
export const loginWithProvider = async (provider) => {
  // Native Google/Apple: use native SDK for seamless sign-in
  if (platform.isNative && provider === 'google') {
    return nativeGoogleLogin();
  }
  // Apple native SDK only on iOS; Android falls through to browser OAuth flow
  if (platform.isIOS && provider === 'apple') {
    return nativeAppleLogin();
  }

  // GitHub on mobile: use browser flow with deep link callback
  // Web: standard OAuth redirect
  const redirectTo = platform.isNative
    ? 'reedle://auth-callback'
    : `${window.location.origin}/`;

  if (platform.isNative) {
    // Open OAuth URL in system browser, handle callback via deep link
    const { Browser } = await import('@capacitor/browser');
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        skipBrowserRedirect: true,
      },
    });
    if (error) throw error;
    if (data?.url) {
      await Browser.open({ url: data.url });
    }
    return data;
  }

  // Web: standard redirect flow
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
    },
  });
  if (error) throw error;
  return data;
};

/**
 * Get all identities linked to the current user.
 * Returns array of { id, provider, identity_data, ... }
 */
export const getUserIdentities = async () => {
  const { data, error } = await supabase.auth.getUserIdentities();
  if (error) throw error;
  return data?.identities || [];
};

/**
 * Link a new OAuth provider to the currently logged-in user.
 * On native mobile: Google/Apple use native SDK (no browser).
 * GitHub and web use browser-based redirect flow.
 * @param {'google'|'apple'|'github'} provider
 */
export const linkIdentity = async (provider) => {
  // Note: signInWithIdToken for linking is not directly supported by Supabase.
  // Native Google/Apple linking still uses the browser redirect flow on mobile
  // since linkIdentity requires the server-side OAuth handshake.
  const redirectTo = platform.isNative
    ? 'reedle://auth-callback'
    : `${window.location.origin}/`;

  if (platform.isNative) {
    const { Browser } = await import('@capacitor/browser');
    const { data, error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo,
        skipBrowserRedirect: true,
      },
    });
    if (error) throw error;
    if (data?.url) {
      await Browser.open({ url: data.url });
    }
    return data;
  }

  const { data, error } = await supabase.auth.linkIdentity({
    provider,
    options: {
      redirectTo,
    },
  });
  if (error) throw error;
  return data;
};
